import axios from 'axios';
import { Globals } from '@/modules/Globals';
import { ASSISTANT_SCHEMA_VERSION } from '@/interfaces/assistant.interface';
import type {
  IAssistantActivityQuery,
  IAssistantActivityResponse,
  IAssistantApplyRequest,
  IAssistantApplyResult,
  IAssistantConfigResponse,
  IAssistantDiscardResponse,
  IAssistantIdentityTokenRequest,
  IAssistantIdentityTokenResponse,
  IAssistantProposalView,
  IAssistantProposeRequest,
  IAssistantProposeResponse,
  IAssistantSchemaVersionRequest,
  IAssistantUndoResult,
} from '@/interfaces/assistant.interface';

/** Reads, proposals and small writes. */
export const ASSISTANT_TIMEOUT_MS = 15000;
/** Apply and undo write up to 500 products in one transaction. */
export const ASSISTANT_WRITE_TIMEOUT_MS = 30000;

const READ = { timeout: ASSISTANT_TIMEOUT_MS };
const WRITE = { timeout: ASSISTANT_WRITE_TIMEOUT_MS };

const SCHEMA_VERSION_BODY: IAssistantSchemaVersionRequest = {
  schemaVersion: ASSISTANT_SCHEMA_VERSION,
};

/** `/restaurants/{rid}/assistant` plus the given path segments, each URL-encoded. */
function assistantUrl(restaurantId: number, ...segments: string[]): string {
  const path = segments.map(encodeURIComponent).join('/');
  return `${Globals.apiPath}/restaurants/${restaurantId}/assistant/${path}`;
}

/** `restaurants/:restaurantId/assistant`: the «Asistente» Action API (plan §3.8). */
export class AssistantService {
  /** Whether the assistant may prepare changes here, and with which ops. */
  static async getConfig(
    restaurantId: number,
    branchId: number
  ): Promise<IAssistantConfigResponse> {
    const res = await axios.get<IAssistantConfigResponse>(
      `${assistantUrl(restaurantId, 'config')}?branchId=${branchId}`,
      READ
    );
    return res.data;
  }

  /** A short-lived token that tells GPTHelp who is chatting (plan §3.1). */
  static async identityToken(
    restaurantId: number,
    body: IAssistantIdentityTokenRequest
  ): Promise<IAssistantIdentityTokenResponse> {
    const res = await axios.post<IAssistantIdentityTokenResponse>(
      assistantUrl(restaurantId, 'identity-token'),
      body,
      READ
    );
    return res.data;
  }

  static async propose(
    restaurantId: number,
    body: IAssistantProposeRequest
  ): Promise<IAssistantProposeResponse> {
    const res = await axios.post<IAssistantProposeResponse>(
      assistantUrl(restaurantId, 'proposals'),
      body,
      READ
    );
    return res.data;
  }

  static async getProposal(
    restaurantId: number,
    proposalId: string
  ): Promise<IAssistantProposalView> {
    const res = await axios.get<IAssistantProposalView>(
      assistantUrl(restaurantId, 'proposals', proposalId),
      READ
    );
    return res.data;
  }

  /** Renders the proposal again against the live menu. */
  static async refresh(
    restaurantId: number,
    proposalId: string
  ): Promise<IAssistantProposalView> {
    const res = await axios.post<IAssistantProposalView>(
      assistantUrl(restaurantId, 'proposals', proposalId, 'refresh'),
      SCHEMA_VERSION_BODY,
      READ
    );
    return res.data;
  }

  static async apply(
    restaurantId: number,
    proposalId: string,
    body: IAssistantApplyRequest
  ): Promise<IAssistantApplyResult> {
    const res = await axios.post<IAssistantApplyResult>(
      assistantUrl(restaurantId, 'proposals', proposalId, 'apply'),
      body,
      WRITE
    );
    return res.data;
  }

  static async discard(
    restaurantId: number,
    proposalId: string
  ): Promise<IAssistantDiscardResponse> {
    const res = await axios.post<IAssistantDiscardResponse>(
      assistantUrl(restaurantId, 'proposals', proposalId, 'discard'),
      SCHEMA_VERSION_BODY,
      READ
    );
    return res.data;
  }

  /** Puts back what an applied action changed (idempotent: a retry answers `replayed`). */
  static async undo(
    restaurantId: number,
    actionId: string
  ): Promise<IAssistantUndoResult> {
    const res = await axios.post<IAssistantUndoResult>(
      assistantUrl(restaurantId, 'actions', actionId, 'undo'),
      SCHEMA_VERSION_BODY,
      WRITE
    );
    return res.data;
  }

  /** «Cambios del asistente»: the store's applied and undone actions, newest first. */
  static async listActions(
    restaurantId: number,
    query: IAssistantActivityQuery = {}
  ): Promise<IAssistantActivityResponse> {
    const params = new URLSearchParams();
    if (query.before) params.set('before', query.before);
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    const search = params.toString();
    const res = await axios.get<IAssistantActivityResponse>(
      assistantUrl(restaurantId, 'actions') + (search ? `?${search}` : ''),
      READ
    );
    return res.data;
  }
}
